import { cloneDeep, toLower } from 'lodash';
import { fromColumnToStrictColumn } from 'src/types';
import { invokeOrIdentity } from 'src/utils';
import sentenceCase from 'src/utils/stringOperations/sentenceCase';
import removeUndefinedArrayElements from '../arrayOperations/removeUndefinedArrayElements';
import { getMonthNames } from '../dateOperations/getMonthNames';
import getFullTable from '../entityOperations/getFullTable';
function validateColumns(cols) {
    const names = cols.map(c => c.name);
    const uniqueNames = [...new Set(names)];
    if (names.length !== uniqueNames.length) {
        const duplicates = names.filter((item, index) => names.indexOf(item) !== index);
        // duplicate column names cause errors at run time, and cannot be currently constrained by typescript
        // would like to throw error but impact of that on all apps cannot be assessed without operning those apps
        // throw new Error(`Duplicate column names: ${duplicates.join(', ')}`)
        // ... therefore we just console.error
        console.error(`Duplicate column names: ${duplicates.join(', ')}`);
    }
    return cols;
}
function removeDuplicateColumns(cols) {
    const names = cols.map(c => c.name);
    const uniqueNames = [...new Set(names)];
    return cols.filter(c => uniqueNames.includes(c.name));
}
function addLabelIfNeeded(col) {
    if (col.name && (col.label === undefined || col.label === null) && col.name !== 'row_menu') {
        let label = sentenceCase(String(col.name));
        // Remove trailing ' id' from constructed label
        const len = (label || '').length;
        if (label.substring(len - 3) === ' id')
            label = label.substring(0, len - 3);
        // capitalize first letters where needed
        const wordsThatNeedCapitalization = getMonthNames().map(toLower);
        label = label
            .split(' ')
            .map(word => {
            if (wordsThatNeedCapitalization.includes(word)) {
                word = sentenceCase(word);
            }
            return word;
        })
            .join(' ');
        return Object.assign(Object.assign({}, col), { label });
    }
    else {
        return col;
    }
}
export default function enrichColumns(_cols) {
    const cols = invokeOrIdentity(_cols);
    const result = cols.map((_col) => {
        const col = addLabelIfNeeded(fromColumnToStrictColumn(_col));
        if (col.name === 'name') { // because name is a common column name, we want to make sure it is always displayed without browser interference
            col.suppressAutoComplete = true;
        }
        // col.references can be entered as DataRow[] or as LocalEntityName or a string array of possible values
        // If it was entered as LocalEntityName, we need to get its DataRow equivalent, which we do here:
        if (col.references) {
            if (typeof col.references === 'string') {
                col.references = getFullTable(col.references);
            }
            else if (Array.isArray(col.references)) {
                // @ts-ignore
                if (col.references.every((ref) => typeof ref === 'string')) {
                    col.references = col.references.map((ref) => {
                        if (typeof ref !== 'string')
                            return ref;
                        return { id: ref, name: ref };
                    });
                }
                else {
                    // all good
                }
            }
            else if (typeof col.references === 'function') {
                // no check
            }
            else {
                debugger;
                console.error(`Error in column ${col.name}: references must be entered as DataRow[] or as LocalEntityName or a string array of possible values`);
                return;
            }
        }
        // align left by default
        // consider moving this to displayValueGetter
        if (!col.align) {
            if (col.isInteger || col.isNumeric || col.isPercent || col.isCurrency) {
                col.align = 'right';
            }
            else
                col.align = 'left';
        }
        if (col.isCheckbox || col.isToggle || col.isIcon) {
            col.align = 'center';
            col.alignHeader = 'left';
            // @ts-ignore
            col.possibleValues = [true, false];
            col.valueGetter = cellProps => Boolean(cellProps.value);
            col.displayValueGetter = cellProps => Number(cellProps.value);
        }
        if (col.isDate) {
            col.cellDivClasses = 'display-date';
            col.tdClasses = 'display-date';
            col.cellContentClasses = 'display-date';
        }
        if (col.isDateTime) {
            col.cellDivClasses = 'display-date-time';
            col.tdClasses = 'display-date-time';
            col.cellContentClasses = 'display-date-time';
        }
        // sometimes we want to pass display class in data, from the server
        // classes can be passed by the server as a string on field with name like 'target_field__display_class', e.g., sku_first_seen_in_customer_on_date__display_class
        // here we extract that and put it in col.classes
        if (col.field && String(col.field).endsWith('__display_class')) {
            const field = col.field;
            const baseField = String(field).replace('__display_class', '');
            const baseColIdx = cols.findIndex(c => c.field === baseField);
            if (baseColIdx > -1) {
                cols[baseColIdx] = cloneDeep(cols[baseColIdx]);
            }
        }
        if (col.type === 'button') {
            col.align = 'center';
            col.alignHeader = 'center';
            col.isButton = true;
        }
        return col;
    });
    return validateColumns(removeDuplicateColumns(removeUndefinedArrayElements(result)));
}
